import React, { useState } from 'react';
import NumericInput from './NumericInput.js';
import './VentilationSettings.css';
import { SummerModeSection, ScheduledOverridesSection, ManualOverridesSection } from './VentilationSettings_Components.js';
import { t } from './i18n.js';
//import  sampleConfig from './sample_config.json'

const VentilationSettings = ({ initialVentilationConfigs, onSave, saveStatus }) => {
   
  const [ventilationConfig, setVentilationConfig] = useState(initialVentilationConfigs);
  //const [ventilationConfig, setVentilationConfig] = useState(sampleConfig);
  const isIphone = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  //console.log(initialVentilationConfigs)
  if (!ventilationConfig || Object.keys(ventilationConfig).length === 0) {
    return <div>{t('common.loading')}</div>;
  }

  

  const handleInputChange = (controlId, stateName, key, value) => {
    setVentilationConfig((prevConfigs) => {
      const updatedConfigs = prevConfigs.map((control) => {
        if (control.id === controlId) {
          if (key === 'enabled') {
            return {
              ...control,
              enabled: value,
            };
          }

          if (stateName) {
            const updatedStates = control.states.map((state) => {
              if (state.name === stateName) {
                const keys = key.split('.');

                // Handling outdoor_triggers
                if (keys[0].startsWith('outdoor_triggers')) {
                  const match = keys[0].match(/\[(\d+)\]/);
                  if (!match) {
                    console.error("Unable to parse trigger index from key:", key);
                    return state;
                  }

                  const triggerIndex = parseInt(match[1], 10);
                  const triggerKey = keys[2];

                  // Convert co2 or humidity to number
                  const numericValue = triggerKey === 'co2' || triggerKey === 'humidity'
                    ? parseFloat(value) : value;

                  const updatedTriggers = state.outdoor_triggers.map((trigger, index) => {
                    if (index === triggerIndex) {
                      if (!trigger.triggers) {
                        console.error("No triggers found for:", trigger);
                        return trigger;
                      }

                      return {
                        ...trigger,
                        triggers: {
                          ...trigger.triggers,
                          [triggerKey]: numericValue,
                        },
                      };
                    }
                    return trigger;
                  });

                  return {
                    ...state,
                    outdoor_triggers: updatedTriggers,
                  };
                }

                // Handling default_triggers
                if (keys[0] === 'default_triggers') {
                  const triggerKey = keys[1];
                  const numericValue = triggerKey === 'co2' || triggerKey === 'humidity'
                    ? parseFloat(value) : value;

                  return {
                    ...state,
                    default_triggers: {
                      ...state.default_triggers,
                      [triggerKey]: numericValue,
                    },
                  };
                }

                // Handling hysteresis
                if (keys[0] === 'hysteresis') {
                  const hysteresisKey = keys[1];
                  const numericValue = hysteresisKey === 'co2' || hysteresisKey === 'humidity'
                    ? parseFloat(value) : value;

                  return {
                    ...state,
                    hysteresis: {
                      ...state.hysteresis,
                      [hysteresisKey]: numericValue,
                    },
                  };
                }

                return state;
              }
              return state;
            });

            return {
              ...control,
              states: updatedStates,
            };
          }

          return control;
        }
        return control;
      });

      return updatedConfigs;
    });
  };

  const handleManualOverrideChange = (controlId, overrideKey, field, value) => {
    setVentilationConfig(prevConfig => {
      return prevConfig.map(item => {
        if (item.id === controlId) {
          return {
            ...item,
            manual_overrides: {
              ...item.manual_overrides,
              [overrideKey]: {
                ...item.manual_overrides[overrideKey],
                [field]: value
              }
            }
          };
        }
        return item;
      });
    });
  };


  const handleScheduledOverrideChange = (controlId, overrideKey, field, value) => {
    setVentilationConfig(prevConfig => {
      return prevConfig.map(item => {
        if (item.id === controlId) {
          return {
            ...item,
            scheduled_overrides: {
              ...item.scheduled_overrides,
              [overrideKey]: {
                ...item.scheduled_overrides[overrideKey],
                [field]: value
              }
            }
          };
        }
        return item;
      });
    });
  };


  const handleSave = () => {
    // Convert array format to object format with numeric keys
    const formattedConfig = {};
    ventilationConfig.forEach((config, index) => {
      formattedConfig[index.toString()] = config;
    });
    
    onSave(formattedConfig);
  };

  const SensorTable = ({ sensors }) => {
    if (!sensors) return null;

    let sensorTypes = Object.keys(sensors);
    
    // Ensure exactly 3 columns for a consistent layout
    while (sensorTypes.length < 3) {
      sensorTypes.push("");
    }

    const maxLength = Math.max(...Object.values(sensors).map(arr => arr.length), 1);

    return (
      <table className="table">
        <thead>
          <tr>
            <th>{t('ventilation.sensors')}</th>
            {sensorTypes.map((type, index) => (
              <th key={index}>{type ? type.toUpperCase() : ""}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: maxLength }).map((_, index) => (
            <tr key={index}>
              <td>{index === 0 ? " " : ""}</td>
              {sensorTypes.map((type, idx) => (
                <td key={idx}>{sensors[type]?.[index] || ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Separate summer_mode from other controls
  const summerMode = ventilationConfig.find(item => item.id === 'summer_mode');
  const otherControls = ventilationConfig.filter(item => item.id !== 'summer_mode');

  return (
    <div className="container">
      <h2>{t('ventilation.title')}</h2>

      {/* Summer Mode Section */}
      <SummerModeSection 
        summerMode={summerMode} 
        onInputChange={handleInputChange} 
      />


      {(Array.isArray(otherControls) ? otherControls : Object.values(otherControls))
        .map((control) => (
          <div key={control.id} className="control-section">
            <h3>{control.name}</h3>
            <label className="control-label">
              {t('common.enabled')}:
              <input
                type="checkbox"
                checked={control.enabled}
                onChange={(e) => handleInputChange(control.id, null, 'enabled', e.target.checked)}
              />
            </label>
            
            <SensorTable sensors={control.sensors} />

            {/* Scheduled Overrides Section */}
            <ScheduledOverridesSection 
              control={control} 
              onScheduledOverrideChange={handleScheduledOverrideChange} 
            />

            {/* Manual Overrides Section */}
            <ManualOverridesSection 
              control={control} 
              onManualOverrideChange={handleManualOverrideChange} 
            />
            
            {control.states.map((state) => (
              <div key={state.name}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('ventilation.triggerLevel')}</th>
                      <th>{state.name}</th>
                      <th></th>
                    </tr>
                  </thead>  
                  <tbody>
                    <tr>
                      {state.outdoor_triggers && (
                        <>
                          <td>{t('ventilation.outdoorTemp')}</td>
                          {state.outdoor_triggers[0].triggers.co2 !== undefined && (
                            <td>{t('ventilation.co2Exceeds')}</td>
                          )}
                          {state.outdoor_triggers[0].triggers.humidity !== undefined && (
                            <td>{t('ventilation.humidityExceeds')}</td>
                          )}
                        </>
                      )}
                      {state.default_triggers && (
                        <>
                          <td></td>
                          {state.default_triggers.co2 !== undefined && (
                            <td>{t('ventilation.co2Exceeds')}</td>
                          )}
                          {state.default_triggers.humidity !== undefined && state.condition !== 'max' && (
                            <td>{t('ventilation.humidityExceeds')}</td>
                          )}
                          {state.default_triggers.humidity !== undefined && state.condition === 'max' &&(
                            <td>{t('ventilation.humidityBelow')}</td>
                          )}
                        </>
                      )}
                    </tr>
                    
                    {state.hysteresis && (
                      <>
                        <td></td>
                        {state.hysteresis.co2 !== undefined && (
                          <td>{t('ventilation.co2Ppm')}</td>
                        )}
                        {state.hysteresis.humidity !== undefined && (
                          <td>{t('ventilation.humidityPercent')}</td>
                        )}
                      </>
                    )}

                    {/* Render Outdoor Triggers */}
                    {state.outdoor_triggers && state.outdoor_triggers.map((trigger, index) => (
                      <tr key={index}>
                        <td>{trigger.range}</td>
                        <td>
                          <NumericInput 
                            value={trigger.triggers.co2} 
                            onChange={(value) => handleInputChange(control.id, state.name, `outdoor_triggers[${index}].triggers.co2`, value)}
                            step={25}
                            min={400}
                            max={2000}
                          />
                        </td>
                        <td>
                          <NumericInput 
                            value={trigger.triggers.humidity} 
                            onChange={(value) => handleInputChange(control.id, state.name, `outdoor_triggers[${index}].triggers.humidity`, value)}
                            step={5}
                            max={95}
                            min={5}
                          />
                        </td>
                      </tr>
                    ))}
                    
                    {/* Render Default Triggers */}
                    {state.default_triggers && (
                      <tr>
                        <td>{t('ventilation.triggerValue')}</td>
                        {state.default_triggers.co2 !== undefined && (
                          <td>
                            <NumericInput 
                              value={state.default_triggers.co2} 
                              onChange={(value) => handleInputChange(control.id, state.name, `default_triggers.co2`, value)}
                              step={25}
                              min={400}
                              max={2000}
                            />
                          </td>
                        )}
                        {state.default_triggers.humidity !== undefined && (
                          <td>
                            <NumericInput 
                              value={state.default_triggers.humidity} 
                              onChange={(value) => handleInputChange(control.id, state.name, `default_triggers.humidity`, value)}
                              step={5}
                              max={95}
                              min={5}
                            />
                          </td>
                        )}
                      </tr>
                    )}
                    
                    {/* Render hysteresis based triggers */}
                    {state.hysteresis && (
                      <tr>
                        <td>{t('ventilation.switchBackDiff')}</td>
                        {state.hysteresis.co2 !== undefined && (
                          <td>
                            <NumericInput 
                              value={state.hysteresis.co2} 
                              onChange={(value) => handleInputChange(control.id, state.name, `hysteresis.co2`, value)} 
                              step={25}
                              min={-200}
                              max={-50}
                            />
                          </td>
                        )}
                        {state.hysteresis.humidity !== undefined && (
                          <td>
                            <NumericInput 
                              value={state.hysteresis.humidity} 
                              onChange={(value) => handleInputChange(control.id, state.name, `hysteresis.humidity`, value)} 
                              step={5}
                              min={-20}
                              max={20}
                            />
                          </td>
                        )}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ))}

        {saveStatus === 'success' && <p><i>{t('common.saveSuccess')}</i></p>}
        {saveStatus === 'error' && <p>{t('common.saveError')}</p>}
        <button onClick={handleSave}>{t('common.save')}</button>
    </div>
  );
};

export default VentilationSettings;